import Switch from "@imminently/decisively-ui/Switch";
import { useEffect, useRef, useState } from "react";
import { getOpenAIKey, setOpenAIKey } from "@/features/ai";
import { usePreferences } from "../PreferencesContext";
import type { FeatureSettings } from "@/config/features";

type ExperimentalKey = keyof FeatureSettings["experimental"];

interface FeatureDef {
  key: ExperimentalKey;
  label: string;
  description: string;
}

const FEATURES: FeatureDef[] = [
  {
    key: "ai_chat",
    label: "AI Features",
    description: "Enable experimental AI-powered features, such as chat assistance for rule authoring",
  },
  {
    key: "extended_debug_diagnostics",
    label: "Extended Debug Diagnostics",
    description: "Show extended node diagnostics, including detailed reason metadata in debug views",
  },
  {
    key: "web_ingestion",
    label: "Web Ingestion",
    description: "Enable loading content from web pages or PDF documents as context for rules and AI features",
  },
  {
    key: "simplified_relationship_dialog",
    label: "New Relationship Dialog",
    description: "Use the new relationship dialog",
  },
  {
    key: "release_centre_tabs",
    label: "Release Centre Tabs",
    description: "Use the experimental release centre layout with separate development and production tabs",
  },
  {
    key: "tasks",
    label: "Tasks",
    description: "Enable the tasks panel in the header for managing and tracking tasks",
  },
  {
    key: "interview_sidebars",
    label: "Interview Sidebars",
    description: "Enable sidebars in interviews for displaying additional information such as help text, supporting documents, or source legislation",
  },
];

const OpenAIKeyInput = () => {
  const [value, setValue] = useState("");
  const loadedRef = useRef(false);

  useEffect(() => {
    if (!loadedRef.current) {
      loadedRef.current = true;
      setValue(getOpenAIKey() ?? "");
    }
  }, []);

  return (
    <div className="mt-3 space-y-1">
      <label htmlFor="openai-api-key" className="block text-sm font-medium">
        OpenAI API Key
      </label>
      <input
        id="openai-api-key"
        type="password"
        className="w-full rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
        placeholder="sk-..."
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onBlur={() => setOpenAIKey(value)}
      />
      <p className="text-xs text-muted-foreground">Stored in session storage. Cleared when the tab is closed.</p>
    </div>
  );
};

export const ExperimentalPanel = () => {
  const { featureSettings, toggleFeature } = usePreferences();

  return (
    <div className="p-8 space-y-6 max-w-2xl">
      <div>
        <h2 className="text-2xl font-bold">Experimental Features</h2>
        <p className="text-muted-foreground mt-1">These features are experimental and may not be stable</p>
      </div>

      <div className="space-y-2">
        {FEATURES.map((feature) => (
          <div
            key={feature.key}
            className="flex flex-col px-4 py-4 rounded-lg border bg-card gap-2"
          >
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <label
                  htmlFor={`experimental.${feature.key}`}
                  className="block text-sm font-medium cursor-pointer mb-1"
                >
                  {feature.label}
                </label>
                <p className="text-sm text-muted-foreground">{feature.description}</p>
              </div>
              <Switch
                id={`experimental.${feature.key}`}
                checked={featureSettings.experimental[feature.key]}
                onCheckedChange={() => toggleFeature(`experimental.${feature.key}`)}
              />
            </div>
            {feature.key === "ai_chat" && featureSettings.experimental.ai_chat && (
              <OpenAIKeyInput />
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
